import { Avatar, Box, Chip, TableCell, TableRow, useTheme } from '@material-ui/core';
import { formatDateTimeReadable, Role, User } from '@nemuru-eng/js-api-client';

import { Font } from '@/components/shared/components/Font';
import { Icon } from '@/components/shared/components/Icon';
import { MoreActionsMenuWithIcon } from '@/components/shared/components/MoreActionsMenu/MoreActionsMenuWithIcon';
import { ActionsOption } from '@/components/shared/components/MoreActionsMenu/types/ActionsOption';
import { SizedIconWithCircle } from '@/components/shared/components/SizedIconWithCircle';
import { StatusTag } from '@/components/shared/components/StatusTag/StatusTag';
import { Text } from '@/components/shared/components/Text';
import { SPY_USER_EMAIL } from '@/components/shared/components/UserList/constants/SpyUserEmail';
import { useUserTable } from '@/components/shared/components/UserList/hooks/useUserTable';
import { UserListColumn, UserListTableProps } from '@/components/shared/components/UserList/types/TableProps';
import { UserRowActionType } from '@/components/shared/components/UserList/types/UserRowActionType';
import { StatusTagType } from '@/config/types';
import { useUserContext } from '@/contexts/UserContext/UserContext';
import UserInMerchantStructure from '@/entities/UserInMerchantStructure';
import { useScreen } from '@/hooks/useScreen';

interface UserTableRowProps extends UserListTableProps {
  user: User;
  editModeAllowed: boolean;
  handleRowUserAction: ({ user, userAction }) => void;
}

export const UserTableRow = ({ user, editModeAllowed, handleRowUserAction, tableProps }: UserTableRowProps) => {
  const { palette } = useTheme();
  const { classes, columns, hideColumn } = useUserTable({ tableProps });
  const { isMobile } = useScreen();
  const { structure, userEntity } = useUserContext();

  const unblockedUserActions = (): ActionsOption[] => {
    const actions: ActionsOption[] = userEntity?.isRoleMerchantChannelOrAgent()
      ? [
          {
            display: <Text tId={'SEND_FORGOT_PASSWORD_EMAIL'} />,
            iconName: 'send',
            disabled: user.username === SPY_USER_EMAIL,
            onClick: () => handleRowUserAction({ user, userAction: UserRowActionType.SEND_FORGOT_PASSWORD_EMAIL }),
          },
          {
            display: <Text tId={'SEE_PASSWORD_RECOVERY_LIST'} />,
            iconName: 'email',
            onClick: () => handleRowUserAction({ user, userAction: UserRowActionType.SEE_PASSWORD_RECOVERY_LIST }),
            divider: true,
          },

          ...(userEntity.hasRole([Role.ROLE_MERCHANT, Role.ROLE_MERCHANT_GROUP])
            ? [
                {
                  display: <Text tId={'CHANGE_CHANNEL'} />,
                  iconName: 'move_up',
                  disabled: false,
                  onClick: () => handleRowUserAction({ user, userAction: UserRowActionType.CHANGE_TO_ANOTHER_CHANNEL }),
                },
              ]
            : []),

          {
            display: <Text tId={'BLOCK_USER'} />,
            iconName: 'lock',
            disabled: user.username === SPY_USER_EMAIL,
            onClick: () => handleRowUserAction({ user, userAction: UserRowActionType.BLOCK_USER }),
          },
        ]
      : [
          {
            display: <Text tId={'SEND_FORGOT_PASSWORD_EMAIL'} />,
            iconName: 'send',
            disabled: user.username === SPY_USER_EMAIL,
            onClick: () => handleRowUserAction({ user, userAction: UserRowActionType.SEND_FORGOT_PASSWORD_EMAIL }),
          },
          {
            display: <Text tId={'SEE_PASSWORD_RECOVERY_LIST'} />,
            iconName: 'email',
            onClick: () => handleRowUserAction({ user, userAction: UserRowActionType.SEE_PASSWORD_RECOVERY_LIST }),
            divider: true,
          },
          {
            display: <Text tId={'CHANGE_USER'} />,
            iconName: 'move_up',
            disabled: false,
            onClick: () => handleRowUserAction({ user, userAction: UserRowActionType.CHANGE_USER }),
          },
          {
            display: <Text tId={'CHANGE_USER_BY_ID'} />,
            iconName: 'move_up',
            disabled: false,
            onClick: () => handleRowUserAction({ user, userAction: UserRowActionType.CHANGE_USER_BY_ID }),
            divider: true,
          },
          {
            display: <Text tId={'CHANGE_PASSWORD'} />,
            iconName: 'password',
            disabled: user.username === SPY_USER_EMAIL,
            onClick: () => handleRowUserAction({ user, userAction: UserRowActionType.CHANGE_PASSWORD }),
          },
          {
            display: user.isRequiredTwoFactor ? (
              <Text tId={'REMOVE_TWO_FACTOR_AUTH'} />
            ) : (
              <Text tId={'ADD_TWO_FACTOR_AUTH'} />
            ),
            iconName: 'pin',
            onClick: () =>
              handleRowUserAction({
                user,
                userAction: user.isRequiredTwoFactor
                  ? UserRowActionType.REMOVE_TWO_FACTOR_AUTH
                  : UserRowActionType.ADD_TWO_FACTOR_AUTH,
              }),
          },
          {
            display: <Text tId={'BLOCK_USER'} />,
            iconName: 'lock',
            disabled: user.username === SPY_USER_EMAIL,
            onClick: () => handleRowUserAction({ user, userAction: UserRowActionType.BLOCK_USER }),
          },
        ];
    return actions;
  };

  const blockedUserActions = (): ActionsOption[] => {
    return [
      {
        display: <Text tId={'UNBLOCK_USER'} />,
        iconName: 'lock_open',
        onClick: () => handleRowUserAction({ user, userAction: UserRowActionType.UNBLOCK_USER }),
      },
    ];
  };

  const getUserName = (user: User) => {
    if (!tableProps?.users || !structure) return;
    return new UserInMerchantStructure(user, structure).name;
  };

  return (
    <>
      <TableRow key={user.id} className={classes.root}>
        {columns.some((column) => column.name === 'userId') && !hideColumn(UserListColumn.ICON) && (
          <TableCell align={'left'}>
            <Box p={1} display={'flex'} alignItems={'center'} justifyContent={'center'}>
              <SizedIconWithCircle
                variant={'small'}
                backgroundColor={palette.secondary.light}
                iconColor={palette.secondary.contrastText}
                customBorderRadius={100}
                iconName={'key'}
                copyToClipboard={{
                  value: user.id,
                  textId: 'COPY_USER_ID',
                }}
              />
            </Box>
          </TableCell>
        )}
        {tableProps?.users && (
          <TableCell align={'left'} width={'220px'}>
            <Font variant={'body2'} color={'textPrimary'}>
              {getUserName(user) ?? '-'}
            </Font>
          </TableCell>
        )}
        <TableCell align={'left'}>
          <Box
            className={'shorted-cell simple-select overflow-text-ellipsis'}
            display={'flex'}
            alignItems={'center'}
            style={{ maxWidth: isMobile ? 'auto' : '250px', height: 'auto' }}
          >
            <Font className="overflow-text-ellipsis" variant={'body2'} color={'textPrimary'}>
              {user.username}
            </Font>
            {user.username === SPY_USER_EMAIL && (
              <Box ml={1}>
                <Avatar
                  style={{
                    width: 24,
                    height: 24,
                    backgroundColor: palette.primary.main,
                    color: palette.primary.contrastText,
                  }}
                >
                  <i className={'material-icons-outlined'} style={{ fontSize: 16 }}>
                    bug_report
                  </i>
                </Avatar>
              </Box>
            )}
          </Box>
        </TableCell>
        <TableCell align={'center'} style={{ verticalAlign: 'middle', justifyContent: 'center', alignItems: 'center' }}>
          {user.isRequiredTwoFactor ? (
            <Icon color={palette.success.main}>check</Icon>
          ) : (
            <Icon color={palette.error.main}>close</Icon>
          )}
        </TableCell>
        {!hideColumn(UserListColumn.ROLES) && (
          <TableCell align={'left'} style={{ verticalAlign: 'baseline' }}>
            {user.roles.map((role) => (
              <Chip
                key={`${user.id}-${role}`}
                label={<Text tId={role} />}
                variant={'default'}
                style={{
                  marginRight: '6px',
                  marginTop: '6px',
                  backgroundColor: palette.grey[100],
                  color: palette.text.primary,
                }}
              />
            ))}
          </TableCell>
        )}
        {columns.some((column) => column.name === 'blocked') && (
          <TableCell align={'left'} width={'100px'}>
            <StatusTag
              labelTextId={user.isBlocked ? 'BLOCKED' : 'UNBLOCKED'}
              type={user.isBlocked ? StatusTagType.ERROR : StatusTagType.SUCCESS}
            />
          </TableCell>
        )}
        {columns.some((column) => column.name === 'createdAt') && (
          <TableCell align={'left'} width={'200px'}>
            <Font variant={'body2'} color={'textPrimary'}>
              {formatDateTimeReadable(user.createdAt)}
            </Font>
          </TableCell>
        )}
        {editModeAllowed && (
          <TableCell align="right">
            <MoreActionsMenuWithIcon
              actionsOptionsArray={user.isBlocked ? blockedUserActions() : unblockedUserActions()}
            />
          </TableCell>
        )}
      </TableRow>
    </>
  );
};
